import { useEffect, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ICliJob } from 'cli/types';
import { NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { useGetLeadTimeSuggestions, useQuotePartActions } from 'quotes/hooks';
import { mapCreateQuotePartNotesToServer } from 'quotes/mappers';
import { IQuotePart, IQuotePartNoteForm } from 'quotes/types';

import { QuotePartAddNoteForm } from './QuotePartAddNoteForm';
import { quotePartAddNoteSchema } from './schema';
import { getAvailableNoteTypes, getDefaultValues } from './utils';

interface IProps {
  isOpen: boolean;
  level?: number;
  onClose: (closeSelection?: boolean) => void;
  quoteParts: IQuotePart[];
  hasUndiscardableNote?: boolean;
  onSubmit?: (reset?: UseFormReset<IQuotePartNoteForm>) => (data: IQuotePartNoteForm) => void;
  onSubmitSuccess?: () => void;
  entityType?: QuoteEntityType;
  notificationPosition?: NotificationPosition;
  isAlternateVersionEnabled?: boolean;
  quoteType?: QuoteType;
}

export const QuotePartAddNote = ({
  isOpen = false,
  level = 0,
  onClose,
  quoteParts,
  hasUndiscardableNote = undefined,
  onSubmit = null,
  onSubmitSuccess = null,
  entityType,
  notificationPosition = NotificationPosition.FORM,
  // Named for provenance: the quote's flag, which every line mirrors.
  isAlternateVersionEnabled: isQuoteAlternateVersionEnabled = false,
  quoteType,
}: IProps) => {
  const { t } = useTranslation();
  const { showSuccessNotification } = useNotificationContext();
  const { updateRows } = useAgGridClientTableContext();

  const { handleCliJob } = useCliJobHandler();
  const { loadLeadTimeSuggestions, leadTime } = useGetLeadTimeSuggestions();
  const { upsertQuotePartNotes } = useQuotePartActions();

  const availableNoteTypes = useMemo(
    () => getAvailableNoteTypes(isQuoteAlternateVersionEnabled, quoteType),
    [isQuoteAlternateVersionEnabled, quoteType]
  );

  useEffect(() => {
    const loadSuggestions = async () => {
      const selectedQuotePart = quoteParts?.[0];

      await loadLeadTimeSuggestions({
        quotePartId: selectedQuotePart?.id,
        quoteId: selectedQuotePart?.quoteId,
        partNumber: selectedQuotePart?.partArr?.number,
        manufacturerId: selectedQuotePart?.partArr?.manufacturerId,
      });
    };

    if (isOpen && quoteParts?.length === 1) {
      loadSuggestions();
    }
  }, [isOpen, loadLeadTimeSuggestions, quoteParts]);

  const defaultValues = useMemo(
    () => getDefaultValues(quoteParts, hasUndiscardableNote),
    [quoteParts, hasUndiscardableNote]
  );

  // Bulk Add Note pushes its notifications at TOP, outside the drawer; the single-line drawer
  // keeps them at FORM, inside it. That is what decides whether the drawer closes before the job
  // runs or has to stay open long enough for the notification to be read.
  const isNotificationOutsideDrawer = NotificationPosition.TOP === notificationPosition;

  const handleOnSubmit =
    (reset: UseFormReset<IQuotePartNoteForm>) => async (data: IQuotePartNoteForm) => {
      if (onSubmit) {
        onSubmit(reset)(data);
        handleOnClose(reset);

        return;
      }

      // Mapped up front so the payload cannot be affected by the reset the close performs.
      const payload = mapCreateQuotePartNotesToServer(quoteParts, data, leadTime);

      // Mirrors QuotePartRemoveNotes: with the notifications living outside the drawer, the drawer
      // goes before the job is enqueued, so its in-progress and success notifications never play
      // out behind an open drawer. The grid selection is deliberately kept until the job
      // succeeds — it is the user's only way to retry a request or a job that fails.
      if (isNotificationOutsideDrawer) {
        handleOnClose(reset);
      }

      handleCliJob(upsertQuotePartNotes, payload, {
        cliJobInProgressMessage: t('messageLineNotesCreateInProgress', {
          count: quoteParts.length,
        }),
        onCliJobFinished: (cliJob: ICliJob) => {
          const { quoteParts } = cliJob.result as Record<string, IQuotePart[]>;

          updateRows({ update: quoteParts });

          showSuccessNotification(
            {
              message: t('messageLineNotesCreateSuccess', { count: quoteParts.length }),
            },
            notificationPosition
          );

          onSubmitSuccess && onSubmitSuccess();

          if (isNotificationOutsideDrawer) {
            // The drawer went at submit — this only releases the grid selection.
            onClose(true);

            return;
          }

          // The notification renders inside the drawer, so hold it open long enough to be read.
          setTimeout(() => {
            handleOnClose(reset, true);
          }, 1000);
        },
        notificationPosition,
      });
    };

  const handleOnClose = (reset?: UseFormReset<IQuotePartNoteForm>, closeSelection = false) => {
    reset();
    onClose(closeSelection);
  };

  return (
    <FormProvider
      schema={quotePartAddNoteSchema(availableNoteTypes)}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <QuotePartAddNoteForm
        isOpen={isOpen}
        level={level}
        onClose={handleOnClose}
        onSubmit={handleOnSubmit}
        types={availableNoteTypes}
        leadTime={leadTime}
        disableUndiscardableItem={defaultValues.undiscardableItem}
        entityType={entityType}
        isAlternateVersionEnabled={isQuoteAlternateVersionEnabled}
      />
    </FormProvider>
  );
};
